/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl-next';
import { withAnalyticsContext } from '@atlaskit/analytics-next';
import Modal, {
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
	ModalBody,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button';
import { jsx } from '@atlaskit/css';
import { Text } from '@atlaskit/primitives/compiled';

import { type SlackWorkspace, AnalyticsSource } from '../../types';
import { messages } from '../i18n';
import { useAnalytics } from '../../analytics';
import { getConnectedSlackWorkSpace } from './localStorageUtils';
import SlackWorkspacesDropdown from './SlackWorkspacesDropdown';
import DrawerDialogZIndexSetter from './DrawerDialogZIndexSetter';
import { useThirdPartyState } from './ThirdPartyContext';
import { ThirdPartyAnalytics } from './ThirdPartyAnalytics';

const SlackWorkspacesDialog = () => {
	const analytics = useAnalytics(ThirdPartyAnalytics);
	const { formatMessage } = useIntl();

	const connectedSlackWorkspaceId = getConnectedSlackWorkSpace();

	const {
		slackWorkspaces,
		closeSlackConnectDialog,
		isSlackConnectDialogOpen,
		onChangeSelectedSlackWorkspace,
	} = useThirdPartyState();

	const [selectedOption, setSelectedOption] = useState<SlackWorkspace | null>(null);

	useEffect(() => {
		setSelectedOption(
			slackWorkspaces.find((w) => w.id === connectedSlackWorkspaceId) || slackWorkspaces[0],
		);
	}, [slackWorkspaces, connectedSlackWorkspaceId]);

	const onConnectSlackWorkspaceButton = () => {
		if (selectedOption) {
			analytics.slackDoneButtonClicked(selectedOption.id);
			if (selectedOption.id !== connectedSlackWorkspaceId) {
				onChangeSelectedSlackWorkspace(selectedOption);
			}
			closeSlackConnectDialog();
		}
	};

	useEffect(() => {
		if (isSlackConnectDialogOpen) {
			analytics.slackConnectModalShown();
		}
	}, [analytics, isSlackConnectDialogOpen]);

	const ConnectModalHeader = () => {
		const { formatMessage } = useIntl();
		return (
			<ModalHeader hasCloseButton>
				<ModalTitle>
					{formatMessage(
						connectedSlackWorkspaceId
							? messages.slackWorkspacesManageDialogHeading
							: messages.slackWorkspacesConnectDialogHeading,
					)}
				</ModalTitle>
			</ModalHeader>
		);
	};

	const ConnectModalFooter = () => {
		const { formatMessage } = useIntl();

		return (
			<ModalFooter>
				<Button
					type="submit"
					appearance="primary"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					style={{ marginLeft: 'auto' }}
					isDisabled={!Boolean(selectedOption)}
					onClick={onConnectSlackWorkspaceButton}
				>
					{formatMessage(messages.slackWorkspacesDoneButton)}
				</Button>
			</ModalFooter>
		);
	};

	return (
		<>
			{slackWorkspaces && (
				<ModalTransition>
					{isSlackConnectDialogOpen && (
						<Modal
							width={'small'}
							shouldScrollInViewport
							onClose={closeSlackConnectDialog}
							testId="slack-workspace-dialog"
						>
							<ConnectModalHeader />
							<ModalBody>
								<Text as="p">{formatMessage(messages.slackWorkspacesDialogBody)}</Text>
								<br />
								<SlackWorkspacesDropdown
									slackWorkspaces={slackWorkspaces}
									selectedOption={selectedOption}
									onSelect={setSelectedOption}
								/>
								<DrawerDialogZIndexSetter />
							</ModalBody>
							<ConnectModalFooter />
						</Modal>
					)}
				</ModalTransition>
			)}
		</>
	);
};

const analyticsContextData = {
	source: AnalyticsSource.SLACK_CONNECT_MODAL,
	componentName: 'slackConnectModal',
};

export default withAnalyticsContext(analyticsContextData)(SlackWorkspacesDialog);
